/**
 * Fleet — the shop's own vehicles and who has them.
 *
 * The schedule is a Gantt rather than a month grid because a courtesy car sits
 * with one customer for a whole repair: the useful question is which cars are
 * tied up and where the gaps are. The month calendar is kept for the
 * availability-at-a-glance view.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
  subMonths
} from 'date-fns';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';

import PageHeaderActions from '../../components/PageHeaderActions';
import FleetGantt from '../../components/fleet/FleetGantt';
import FleetCalendar from '../../components/fleet/FleetCalendar';
import FleetOverview from '../../components/fleet/FleetOverview';
import FleetVehiclesTable from '../../components/fleet/FleetVehiclesTable';
import FleetBookingsTable, { BOOKING_FILTERS } from '../../components/fleet/FleetBookingsTable';
import { BOOKING_TONE_STYLES, LEGEND_TONES } from '../../components/fleet/fleetStyles';
import FleetVehicleForm from '../overlays/FleetVehicleForm';
import FleetBookingForm from '../overlays/FleetBookingForm';

import { useFleetRealtime } from '../../hooks/useFleetRealtime';
import { useObfuscate } from '../../hooks/useObfuscate';
import { fleetBookingUpdate, fleetVehicleReorder } from '../../services/fleet';
import { VEHICLE_CATEGORIES, bookingVehicleId, vehicleLabel } from '../../lib/fleet';
import { cn } from '../../lib/utils';

import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { SegmentedControl } from '../../components/ui/segmented';
import { Skeleton } from '../../components/ui/skeleton';

const WEEK_STARTS_ON = 1;

const ZOOMS = [
  { value: 'week', label: 'Week', days: 7 },
  { value: 'fortnight', label: '2 weeks', days: 14 },
  { value: 'month', label: 'Month', days: null }
];

const loadPrefs = () => {
  try {
    return JSON.parse(localStorage.getItem('fleetPrefs') || '{}');
  } catch {
    return {};
  }
};

const Fleet = () => {
  const priv = useObfuscate();
  const prefs = useMemo(loadPrefs, []);

  const [tab, setTab] = useState(prefs.tab || 'schedule');
  const [zoom, setZoom] = useState(prefs.zoom || 'fortnight');
  const [anchor, setAnchor] = useState(new Date());
  const [filters, setFilters] = useState({ category: 'all', search: '', ...(prefs.filters || {}) });
  const [bookingFilters, setBookingFilters] = useState({
    status: 'current',
    search: '',
    ...(prefs.bookingFilters || {})
  });

  const [vehicleFormOpen, setVehicleFormOpen] = useState(false);
  const [editingVehicleId, setEditingVehicleId] = useState(null);
  const [bookingFormOpen, setBookingFormOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [bookingDefaults, setBookingDefaults] = useState({});

  // Refreshes each render so overdue state and the "now" marker stay honest
  // without a timer forcing re-renders while nothing is happening.
  const now = new Date();

  const { rangeStart, rangeEnd } = useMemo(() => {
    if (zoom === 'month') {
      return { rangeStart: startOfMonth(anchor), rangeEnd: endOfMonth(anchor) };
    }
    const spec = ZOOMS.find((z) => z.value === zoom) || ZOOMS[1];
    const start = startOfWeek(anchor, { weekStartsOn: WEEK_STARTS_ON });
    return { rangeStart: start, rangeEnd: addDays(start, spec.days - 1) };
  }, [anchor, zoom]);

  // The calendar tab always shows whole months, so it loads its own window.
  const loadWindow = useMemo(() => {
    if (tab === 'calendar') {
      return {
        from: startOfWeek(startOfMonth(anchor), { weekStartsOn: WEEK_STARTS_ON }),
        to: endOfWeek(endOfMonth(anchor), { weekStartsOn: WEEK_STARTS_ON })
      };
    }
    if (tab === 'bookings' || tab === 'overview') {
      // Wide enough that the lists are not truncated by the schedule's window.
      return { from: subMonths(now, 3), to: addMonths(now, 6) };
    }
    return { from: rangeStart, to: rangeEnd };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, anchor, rangeStart, rangeEnd]);

  const { vehicles, bookings, loading, error, refresh, applyLocal, applyLocalVehicle } =
    useFleetRealtime(loadWindow);

  const persist = (patch) => {
    const next = { tab, zoom, filters, bookingFilters, ...patch };
    localStorage.setItem(
      'fleetPrefs',
      JSON.stringify({
        tab: next.tab,
        zoom: next.zoom,
        filters: next.filters,
        // The status is remembered; a search is for this visit only.
        bookingFilters: { status: next.bookingFilters.status }
      })
    );
  };

  const filteredVehicles = useMemo(() => {
    const term = filters.search.trim().toLowerCase();
    return vehicles.filter((v) => {
      if (v.status === 'retired') return false;
      if (filters.category !== 'all' && v.category !== filters.category) return false;
      if (!term) return true;
      return [v.registration, v.displayName, v.make, v.model, v.colour]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(term));
    });
  }, [vehicles, filters]);

  // ---------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------

  const openBooking = (booking) => {
    setEditingBooking(booking);
    setBookingDefaults({});
    setBookingFormOpen(true);
  };

  const createBooking = (defaults = {}) => {
    setEditingBooking(null);
    setBookingDefaults(defaults);
    setBookingFormOpen(true);
  };

  const openVehicle = (vehicle) => {
    setEditingVehicleId(vehicle?.$id || null);
    setVehicleFormOpen(true);
  };

  /**
   * Drag-drop reschedule. Applied locally first so the bar stays where it was
   * dropped, then written; a failure puts it back and says so.
   */
  const handleMoveBooking = useCallback(
    async (booking, patch) => {
      const previous = {
        scheduledStart: booking.scheduledStart,
        scheduledEnd: booking.scheduledEnd,
        vehicle: bookingVehicleId(booking)
      };

      applyLocal({ ...booking, ...patch });

      try {
        const saved = await fleetBookingUpdate(booking.$id, patch);
        applyLocal(saved);

        const movedTo = vehicles.find((v) => v.$id === patch.vehicle);
        toast.success(
          `Moved to ${format(new Date(patch.scheduledStart), 'd MMM')}${movedTo ? ` · ${vehicleLabel(movedTo)}` : ''}`,
          {
            action: {
              label: 'Undo',
              onClick: async () => {
                try {
                  const reverted = await fleetBookingUpdate(booking.$id, previous);
                  applyLocal(reverted);
                } catch {
                  toast.error('Could not undo that');
                }
              }
            }
          }
        );
      } catch (err) {
        console.error('Failed to move booking:', err);
        applyLocal({ ...booking, ...previous });
        toast.error(err?.message || 'Could not move that booking');
      }
    },
    [applyLocal, vehicles]
  );

  const handleReorderVehicles = useCallback(
    async (ordered) => {
      ordered.forEach((v, index) => applyLocalVehicle({ ...v, sortOrder: index }));
      try {
        await fleetVehicleReorder(ordered);
      } catch (err) {
        console.error('Failed to save vehicle order:', err);
        toast.error('Could not save the new order');
        refresh();
      }
    },
    [applyLocalVehicle, refresh]
  );

  const shift = (direction) => {
    if (zoom === 'month') {
      setAnchor((prev) => (direction > 0 ? addMonths(prev, 1) : subMonths(prev, 1)));
      return;
    }
    const spec = ZOOMS.find((z) => z.value === zoom) || ZOOMS[1];
    setAnchor((prev) => addDays(prev, direction * spec.days));
  };

  const rangeLabel =
    tab === 'calendar' || zoom === 'month'
      ? format(anchor, 'MMMM yyyy')
      : `${format(rangeStart, 'd MMM')} – ${format(rangeEnd, 'd MMM yyyy')}`;

  // ---------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------

  const showRangeControls = tab === 'schedule' || tab === 'calendar';

  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-3xl font-bold">Fleet</h1>
        <PageHeaderActions />
      </div>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
          {error}
        </div>
      )}

      <Tabs
        value={tab}
        onValueChange={(v) => {
          setTab(v);
          persist({ tab: v });
        }}
        className="flex min-h-0 flex-1 flex-col gap-4"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="schedule">Schedule</TabsTrigger>
            <TabsTrigger value="calendar">Calendar</TabsTrigger>
            <TabsTrigger value="bookings">Bookings</TabsTrigger>
            <TabsTrigger value="vehicles">Vehicles</TabsTrigger>
          </TabsList>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => openVehicle(null)}>
              <Plus className="mr-1 h-4 w-4" /> Add vehicle
            </Button>
            <Button size="sm" onClick={() => createBooking()}>
              <Plus className="mr-1 h-4 w-4" /> New booking
            </Button>
          </div>
        </div>

        {(tab === 'schedule' || tab === 'calendar' || tab === 'vehicles') && (
          <Card>
            {/* Left to right: what you are looking for, which vehicles, how far
                ahead, and then where. */}
            <CardContent className="flex flex-wrap items-center gap-3 p-4">
              <Input
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                placeholder="Filter by rego, name, make…"
                className="w-64"
              />

              <SegmentedControl
                options={[{ value: 'all', label: 'All' }, ...VEHICLE_CATEGORIES.map((c) => ({ value: c.value, label: c.label }))]}
                value={filters.category}
                onChange={(v) => {
                  const next = { ...filters, category: v };
                  setFilters(next);
                  persist({ filters: next });
                }}
              />

              {tab === 'schedule' && (
                <SegmentedControl
                  options={ZOOMS.map((z) => ({ value: z.value, label: z.label }))}
                  value={zoom}
                  onChange={(v) => {
                    setZoom(v);
                    persist({ zoom: v });
                  }}
                />
              )}

              {showRangeControls && (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="icon" onClick={() => shift(-1)} aria-label="Previous period">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setAnchor(new Date())}>
                      Today
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => shift(1)} aria-label="Next period">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                  <span className="min-w-40 text-sm font-medium">{rangeLabel}</span>
                </div>
              )}

              {tab === 'schedule' && (
                <div className="ml-auto flex flex-wrap items-center gap-3">
                  {LEGEND_TONES.map((tone) => (
                    <span key={tone} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <span className={cn('h-2.5 w-2.5 rounded-full', BOOKING_TONE_STYLES[tone].dot)} />
                      {BOOKING_TONE_STYLES[tone].label}
                    </span>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {tab === 'bookings' && (
          <Card>
            {/* Styled as the vehicles filter above: search first, then which
                bookings. */}
            <CardContent className="flex flex-wrap items-center gap-3 p-4">
              <Input
                value={bookingFilters.search}
                onChange={(e) => setBookingFilters({ ...bookingFilters, search: e.target.value })}
                placeholder="Search customer, rego, notes…"
                className="w-64"
              />

              <SegmentedControl
                options={BOOKING_FILTERS}
                value={bookingFilters.status}
                onChange={(v) => {
                  const next = { ...bookingFilters, status: v };
                  setBookingFilters(next);
                  persist({ bookingFilters: next });
                }}
              />
            </CardContent>
          </Card>
        )}

        <div className="min-h-0 flex-1 overflow-auto">
          <TabsContent value="overview" className="mt-0">
            <FleetOverview
              vehicles={vehicles}
              bookings={bookings}
              loading={loading}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              now={now}
              priv={priv}
              onSelectBooking={openBooking}
              onSelectVehicle={openVehicle}
              onCreateBooking={createBooking}
              onNavigate={(next) => {
                setTab(next);
                persist({ tab: next });
              }}
            />
          </TabsContent>

          <TabsContent value="schedule" className="mt-0">
            <Card className="overflow-hidden p-0">
              {loading ? (
                <div className="space-y-2 p-4">
                  {[...Array(6)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : (
                <FleetGantt
                  vehicles={filteredVehicles}
                  bookings={bookings}
                  rangeStart={rangeStart}
                  rangeEnd={rangeEnd}
                  now={now}
                  priv={priv}
                  onSelectBooking={openBooking}
                  onCreateBooking={createBooking}
                  onMoveBooking={handleMoveBooking}
                />
              )}
              <div className="border-t px-4 py-2 text-xs text-muted-foreground">
                Drag a bar to reschedule it, pull an edge to change the dates, or drag across an empty
                row to start a booking.
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="calendar" className="mt-0">
            <Card className="overflow-hidden p-0">
              {loading ? (
                <Skeleton className="h-96 w-full" />
              ) : (
                <FleetCalendar
                  vehicles={filteredVehicles}
                  bookings={bookings}
                  month={anchor}
                  now={now}
                  onSelectDay={(day) => {
                    setAnchor(day);
                    setZoom('week');
                    setTab('schedule');
                    persist({ tab: 'schedule', zoom: 'week' });
                  }}
                />
              )}
            </Card>
          </TabsContent>

          <TabsContent value="bookings" className="mt-0">
            <Card className="p-0">
              <FleetBookingsTable
                bookings={bookings}
                vehicles={vehicles}
                loading={loading}
                filter={bookingFilters.status}
                search={bookingFilters.search}
                now={now}
                priv={priv}
                onSelect={openBooking}
              />
            </Card>
          </TabsContent>

          <TabsContent value="vehicles" className="mt-0">
            <Card className="p-0">
              <FleetVehiclesTable
                vehicles={filteredVehicles}
                bookings={bookings}
                loading={loading}
                now={now}
                priv={priv}
                onEdit={openVehicle}
                onReorder={handleReorderVehicles}
              />
            </Card>
          </TabsContent>
        </div>
      </Tabs>

      <FleetVehicleForm
        vehicleId={editingVehicleId}
        isOpen={vehicleFormOpen}
        onClose={() => {
          setVehicleFormOpen(false);
          setEditingVehicleId(null);
        }}
        onSaved={(vehicle) => applyLocalVehicle(vehicle)}
        onDeleted={(id) => applyLocalVehicle({ $id: id }, { deleted: true })}
      />

      <FleetBookingForm
        booking={editingBooking}
        isOpen={bookingFormOpen}
        vehicles={vehicles}
        bookings={bookings}
        defaults={bookingDefaults}
        now={now}
        onClose={() => {
          setBookingFormOpen(false);
          setEditingBooking(null);
          setBookingDefaults({});
        }}
        onSaved={(saved) => {
          if (saved?._deleted) applyLocal(saved, { deleted: true });
          else applyLocal(saved);
          refresh();
        }}
      />
    </div>
  );
};

export default Fleet;
